import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Divider, CircularProgress } from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { customGetAPI } from "services/utils/CustomAPI";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { designWeights } from "services/constants/designTokens";
import { dashboard } from "../dashboardStyles";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const availableForLeaveType = (leaveType) => {
  if (leaveType?.available_leave != null) {
    return toNumber(leaveType.available_leave);
  }
  if (leaveType?.balance_leave != null) {
    return toNumber(leaveType.balance_leave);
  }
  return toNumber(leaveType?.credited_leave) - toNumber(leaveType?.used_leave);
};

// Leave Balance KPI tile. Reads the same `leavetypefilter` balance source the
// My Zone > My Leaves screen uses for credited/used totals, so the Home card
// updates after a leave request changes the employee's balance.
export const LeaveBalanceKpi = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [available, setAvailable] = useState(null);
  const [loading, setLoading] = useState(true);

  // "Leaves" is the canonical key in SUB_MODULE_NAME_ALIASES (HelperFunction.js)
  // for the "My Request" module's leave submodule -- matches Leave/index.js's
  // own check. This card previewed the balance and linked to My Leaves for
  // every user, even one with no viewer access to it at all.
  const hasAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Leaves",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchBalance = async () => {
      setLoading(true);
      try {
        const res = await customGetAPI(
          `${domain}leavetypefilter/?user_id=${userDetails?.id}`,
        );
        const leaveTypes = Array.isArray(res?.responseDetails)
          ? res.responseDetails
          : [];
        const value = leaveTypes.reduce(
          (total, leaveType) => total + availableForLeaveType(leaveType),
          0,
        );
        if (!cancelled) setAvailable(value ?? null);
      } catch (error) {
        console.error("[LeaveBalanceKpi] operation failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (userDetails?.id && hasAccess) fetchBalance();
    else setLoading(false);
    return () => {
      cancelled = true;
    };
  }, [domain, userDetails?.id, hasAccess]);

  if (!hasAccess) return null;

  return (
    <Box sx={dashboard.cardSx}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={dashboard.iconWrap(dashboard.colors.purple)}>
          <BeachAccessIcon fontSize="small" />
        </Box>
        <Typography fontWeight={designWeights.semibold} sx={{ color: dashboard.colors.subText }}>
          Leave Balance
        </Typography>
      </Stack>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Typography variant="h5" fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
            {available ?? 0}
          </Typography>
          <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
            Available
          </Typography>
        </>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Typography
        sx={dashboard.linkSx}
        onClick={() => history.push("/admin/myzone/myleaves")}
      >
        View Leave Balance →
      </Typography>
    </Box>
  );
};
